import { IRentsyncSettings } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { IApiResponse, IMetaResponse } from '@/shared';
import { IAction } from '@/shared/interfaces/store-base';
import syndicationSettings from '@/shared/services/syndication-settings';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/rentsync_settings';
const listViewName: string = '';
const showLogging: boolean = false;

const alias = 'rs';

const defaultGetListFilters: IQueryCommandOptions = {
  alias,
  relations: [
    {
      path: 'contentBlocks',
      alias: 'cb',
    },
    {
      path: 'cb.contentBlockTemplate',
      alias: 'cbt',
    },
  ],
  where: [
    {
      field: 'feedKey',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'partnerKey',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'buildingId',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'suiteTypeId',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
  ],
  limit: 999,
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  alias,
  relations: [
    {
      path: 'contentBlocks',
      alias: 'cb',
    },
  ],
  where: [
    {
      field: 'feedKey',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'partnerKey',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'buildingId',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
    {
      field: 'suiteTypeId',
      value: 'null',
      operator: 'null',
      condition: 'and',
      notOperator: false,
    },
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IRentsyncSettings>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'client-syndication-settings/'

  state: {
    ...base.state,

    autoOnSettings: [] as IApiResponse<IRentsyncSettings, IMetaResponse> | [],
    unitExclusionsDisabled: false as boolean,
    // custom state
    // ...{
    // },
  },

  getters: {
    ...base.getters,

    // custom getters
    ...{
      autoOnSettings: (state: any) => [
        ...state.autoOnSettings && state.autoOnSettings.data
          ? state.autoOnSettings.data
          : [],
      ],

      unitExclusionsDisabled: (state: any): boolean => state.unitExclusionsDisabled === true,
    },
  },

  mutations: {
    ...base.mutations,

    // custom mutations
    ...{
      setUnitExclusionsDisabled: (state: any, payload: boolean) => {
        state.unitExclusionsDisabled = payload;
      },

      setAutoOnSettings: (
        state: any,
        payload: IApiResponse<IRentsyncSettings[], IMetaResponse>| IRentsyncSettings[],
      ) => {
        state.autoOnSettings = Array.isArray(payload)
          ? { ...state.autoOnSettings && state.autoOnSettings, ...{ data: payload } }
          : payload;
      },
    },
  },

  actions: {
    ...base.actions,
    async getAutoOn(ctx: IAction<any>) {
      const result = await syndicationSettings.getAutoOnSettings();
      ctx.commit('setAutoOnSettings', result);
    },

    /**
     * Loads the account-wide "Disable 'Exclude Unit from Listings' for all properties" setting so
     * views listing units can tell whether unit-level exclusions may still be changed.
     *
     * `payload` is typed `void` rather than omitted so the generated typed-module wrapper, which
     * always declares a payload parameter, can still be called with no arguments.
     */
    async getUnitExclusionsDisabled(ctx: IAction<any>, payload: void) {
      const result = await syndicationSettings.getDisableUnitExclusions();
      ctx.commit('setUnitExclusionsDisabled', result?.data?.[0]?.value === '1');
    },
  },
};
